/* jshint esversion: 6 */
/* V3 Widget */

/* eslint-env browser */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

// library imports
import Browser from '../lib/Browser';
import IFrameDomain from '../lib/IFrameDomain';
import LocalSession from '../lib/LocalSession';
import * as UUID from '../lib/uuid';
import { AvoCable } from '../lib/avo-cable';

// React imports
import { WebsiteWidgetPopout, WebsiteWidgetForm } from './subcomponents/website_widget_v3';

const STATUSES = ['qualification', 'chatting', 'thank_you'];

class WebsiteWidget extends React.Component {
  constructor(props) {
    super(props);

    const { wsUrl, widgetId, form } = this.props;
    AvoCable.initWithConfig({ url: wsUrl });

    this.key = `avochato-widget-${widgetId}`;
    this.iframeDomain = IFrameDomain.lookup();
    this.state = {
      sessionId: null,
      status: STATUSES[0],
      authenticityToken: form.authenticityToken,
      open: props.open,
      openedAt: null,
      windowStatus: 'hide',
      popoutOpen: false,
      popoutClosed: false,
      initialDomain: this.iframeDomain, // will overwrite if the session has one
    };
    this.websiteWidgetForm = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('message', (e) => this.onWindowMessage(e));
    this.fetchSession();
    if (this.props.autoOpenTimeout > 0) {
      setTimeout(() => {
        // automatically open the widget after a certain period
        // ...unless they explicitly closed the popout already
        // ...or we already opened this widget by any means during this session within the past 5 minutes
        // ...or we are on a mobile device
        // also, don't do it when "opennewtab" to prevent widget being treated like popup spam
        if (!this.props.openNewTab && !this.state.popoutClosed && !Browser.isMobile() && (!this.state.openedAt || (Date.now() - this.state.openedAt >=  5 * 60 * 1000))){
          this.openForm();
        }
      }, this.props.autoOpenTimeout * 1000);
    }
  }

  /* eslint-disable */
  fetchSession() {
    if (Browser.isIframe()) {
      // use parent window as source of truth
      this.sendToParentWindow({type: 'fetchSession'});

      // assume the parent page failed to send data back and generate a new state
      setTimeout(() => {
        if (!this.state.sessionId) {
          console.warn('Failed to fetch session data, regenerating');
          this.setState(this.getCurrentState(), () => this.regenerateSize());
        }
      }, 500);
      return;
    }

    // otherwise use local window as source of truth
    const sessionData = LocalSession.lookup(this.key) || {};
    sessionData.type = 'fetchSession';
    this.onWindowMessage({data: sessionData});
  }
  /* eslint-enable */

  onWindowMessage(event) {
    const currentSession = event.data;
    const { open, enableRecaptcha } = this.props;
    const openState = (currentSession.state && currentSession.state.open) || open;

    switch (currentSession.type) {
      case 'openWithOption':
        this.setState({ open: true }, () => this.regenerateSize());
        if (enableRecaptcha && this.state.status === STATUSES[0]) window.showCaptcha('openWithOption');
        this.websiteWidgetForm.current.setDropDownValue(event.data.value);
        break;
      case 'fetchSession':
        if (!currentSession.state) {
          // no state saved yet, save yourself!
          this.setState(this.getCurrentState(), () => this.regenerateSize());
          break;
        }

        // always try to open
        currentSession.state.open = openState;
        this.setState(currentSession.state, () => this.regenerateSize());
        if (enableRecaptcha && openState && currentSession.state.status === STATUSES[0]) {
          window.showCaptcha('fetchSession');
        }
        break;
    }
  }

  saveSession(reset = false) {
    const currentSession = {
      type: 'saveSession',
      state: this.getCurrentState(),
    };

    if (reset) {
      currentSession.state.open = false;
      currentSession.state.sessionId = UUID.generate();
      [currentSession.state.status] = STATUSES;
      currentSession.state.popoutClosed = false;
    }

    // save it in the parent window for future reference
    this.sendToParentWindow(currentSession);

    // save it locally in case we pop out in the future
    LocalSession.save(this.key, currentSession);

    return currentSession;
  }

  getCurrentState() {
    const { initialDomain, open, popoutClosed, popoutOpen, sessionId, status, openedAt } = this.state;

    return {
      sessionId: sessionId || UUID.generate(),
      status: STATUSES.indexOf(status) < 0 ? STATUSES[0] : status,
      open,
      popoutOpen,
      popoutClosed,
      initialDomain,
      openedAt,
    };
  }

  getIconType() {
    const { iconType } = this.props;
    switch (iconType) {
      case 0:
        return 'circle';
      case 1:
        return 'rounded';
      case 2:
        return 'square';
    }

    return 'circle';
  }

  getBorder() {
    const { borderColor, borderEnabled } = this.props;
    return borderEnabled ? `1px solid ${borderColor}` : null;
  }

  /* eslint-disable */
  render() {
    return (
      <Fragment>
        <WebsiteWidgetForm
          ref={this.websiteWidgetForm}
          widgetId={this.props.widgetId}
          className={this.state.open ? 'opening' : this.state.windowStatus}
          status={this.state.status}
          authenticityToken={this.state.authenticityToken}
          sessionId={this.state.sessionId}
          iframeDomain={this.iframeDomain}
          initialDomain={this.state.initialDomain}
          primaryColor={this.props.primaryColor}
          textColor={this.props.textColor}
          submitButtonColor={this.props.submitButtonColor}
          submitTextColor={this.props.submitTextColor}
          disabled={this.props.form.disabled}
          disabledHTML={this.props.form.disabledHTML}
          countryCode={this.props.form.countryCode || '+1'}
          emailEnabled={this.props.form.emailEnabled}
          dropDownEnabled={this.props.form.dropDownEnabled}
          dropDownPrompt={this.props.form.dropDownPrompt}
          dropDownOptions={this.props.form.dropDownOptions}
          messageEnabled={this.props.form.messageEnabled}
          disclaimer={this.props.form.disclaimer}
          marketingConsentText={this.props.form.marketingConsentText}
          consentMode={this.props.form.consentMode}
          headerText={this.props.form.headerText}
          liveChatText={this.props.form.liveChatText}
          textWithUsText={this.props.form.textWithUsText}
          showNumber={this.props.form.showNumber}
          showNumberThankYou={this.props.form.showNumberThankYou}
          number={this.props.number}
          shadowEnabled={this.props.shadowEnabled}
          whiteLabel={this.props.form.whiteLabel}
          isMobile={Browser.isMobile()}
          onClose={() => this.closeForm()}
          onMinimize={() => this.minimizeForm()}
          onStatusChange={(status) => this.changeStatus(status)} />

        <div id="popout-section" className={this.state.open ? `opened${Browser.isMobile() ? ' mobile' : ''}` : 'closed' } >
          <WebsiteWidgetPopout
            disabled={this.state.popoutClosed}
            popoutText={this.props.popoutText}
            onPopoutClick={ () => { this.toggleForm(); } }
            onPopout={
              () => {
                this.state.popoutOpen = true;
                this.regenerateSize();
              }
            }
            onClose={
              () => {
                this.setState({
                  popoutOpen: false,
                  popoutClosed: true,
                }, () => this.regenerateSize());
              }
            } />
          <div id="revealer" onKeyDown={(e) => { if (e.key == " " || e.code == "Space" || e.keyCode == 32) { this.toggleForm() }} } onClick={() => this.toggleForm()} className={this.state.open ? 'opened ' + this.getIconType() : this.getIconType()} style={{background: this.props.primaryColor, border: this.getBorder()}}  alt={this.state.open ? 'Collapse Live Chat' : 'Launch Live Chat'} aria-label={this.state.open ? 'collapse chat'  : 'open chat'} role="button" tabIndex="0"  >
            <img src={this.props.iconUrl} aria-hidden={this.state.open ? 'true' : '' } alt="Text Us" aria-label='open chat widget' className="main-btn" style={{maxWidth: this.props.iconSize + "%"}} />
            <span className="close-btn"  aria-hidden={this.state.open ? '' : 'true' } aria-label='close chat widget' style={{
              color: this.props.textColor,
              ...this.props.shadowEnabled && { 'text-shadow': '-1px 0 rgba(0,0,0,0.2), 0 1px rgba(0,0,0,0.2), 1px 0 rgba(0,0,0,0.2), 0 -1px rgba(0,0,0,0.2)'},
            }}>×</span>
          </div>
        </div>
      </Fragment>
    );
  }
  /* eslint-enable */

  toggleForm() {
    const { open } = this.state;
    if (open === true) {
      this.minimizeForm();
    } else {
      this.openForm();
    }
  }

  openForm() {
    const { enableRecaptcha, openNewTab, form } = this.props;
    const { width, height } = form;

    if (openNewTab && Browser.isIframe()) {
      let newUrl = window.location.href;
      newUrl += (window.location.search === '') ? '?o=t' : '&o=t';

      let options = 'menubar=no,toolbar=no,width=';
      options += `${width},height=${height}`;
      return window.open(newUrl, 'Live Chat', options);
    }

    this.setState({ open: true, openedAt: Date.now() }, () => {
      this.regenerateSize();
      let nameField = document.getElementById("name");
      if (nameField) {
        nameField.focus();
      }
      let chatField = document.getElementById("chat-message");
      if (chatField) {
        chatField.focus();
      }
    });
    if (enableRecaptcha && this.state.status === STATUSES[0]) window.showCaptcha('openForm');

    return true;
  }

  closeForm() {
    const newSession = this.saveSession(true);
    this.setState(newSession.state, () => this.regenerateSize());
    window.hideCaptcha();

    const { openNewTab } = this.props;
    if (openNewTab && !Browser.isIframe()) window.close();
  }

  minimizeForm() {
    this.setState({ open: false, windowStatus: 'closing' }, () => {
      setTimeout(() => {
        this.regenerateSize();
        this.setState({ windowStatus: 'closing hide' });
      }, 400);
      window.hideCaptcha();
    });
  }

  changeStatus(status) {
    if (!status) return;

    this.setState({ status }, () => this.regenerateSize());
  }

  sendToParentWindow(data) {
    if (this.iframeDomain && Browser.isIframe()) window.parent.postMessage(data, this.iframeDomain);
  }

  regenerateSize() {
    const { open } = this.state;
    if (Browser.isMobile() && open) {
      this.sendToParentWindow({
        type: 'sizeUpdate',
        width: '100%',
        height: '100%',
        bottom: '0px',
        right: '0px',
      });
    } else {
      this.sendToParentWindow({
        type: 'sizeUpdate',
        width: `${this.getWidth()}px`,
        height: `${this.getHeight()}px`,
      });
    }
    this.saveSession();
  }

  getWidth() {
    const { open, popoutOpen } = this.state;
    const { form, frameWidth, hideOnLoad } = this.props;

    if (open) return form.width;

    if (hideOnLoad) return 0;

    return frameWidth + (popoutOpen ? 225 : 0);
  }

  getHeight() {
    const { open, status } = this.state;
    const { form, frameHeight, hideOnLoad } = this.props;

    if (open) {
      switch (status) {
        case 'chatting':
          return 636;
        case 'thank_you':
          return 300 + (form.showNumberThankYou ? 80 : 0);
        default:
          return form.height;
      }
    }

    if (hideOnLoad) return 0;

    return frameHeight;
  }
}

/* eslint-disable */
WebsiteWidget.propTypes = {
  widgetId: PropTypes.string,
  wsUrl: PropTypes.string,

  /* initial open state */
  open: PropTypes.bool,

  /* widget defaults */
  frameWidth: PropTypes.number,
  frameHeight: PropTypes.number,
  iconUrl: PropTypes.string,
  popoutText: PropTypes.string,
  number: PropTypes.string,
  hideOnLoad: PropTypes.bool,
  openNewTab: PropTypes.bool,
  primaryColor: PropTypes.string,
  textColor: PropTypes.string,
  submitButtonColor: PropTypes.string,
  submitTextColor: PropTypes.string,
  iconType: PropTypes.number,
  iconSize: PropTypes.number,
  borderEnabled: PropTypes.bool,
  borderColor: PropTypes.string,
  shadowEnabled: PropTypes.bool,
  enableRecaptcha: PropTypes.bool,

  form: PropTypes.exact({
    width: PropTypes.number,
    height: PropTypes.number,
    disabled: PropTypes.bool,
    disabledHTML: PropTypes.string,
    authenticityToken: PropTypes.string,
    countryCode: PropTypes.string,
    emailEnabled: PropTypes.bool,
    dropDownEnabled: PropTypes.bool,
    dropDownPrompt: PropTypes.string,
    dropDownOptions: PropTypes.array,
    messageEnabled: PropTypes.bool,
    disclaimer: PropTypes.string,
    marketingConsentText: PropTypes.string,
    consentMode: PropTypes.string,
    headerText: PropTypes.string,
    liveChatText: PropTypes.string,
    textWithUsText: PropTypes.string,
    showNumber: PropTypes.bool,
    showNumberThankYou: PropTypes.bool,
    whiteLabel: PropTypes.bool,
    autoOpenTimeout: PropTypes.number,
  }),
};
/* eslint-enable */

export default WebsiteWidget;
